import {
  GEMINI_AMBER,
  GEMINI_AMBER_DARK,
  GEMINI_DARK_BORDER,
  GEMINI_DARK_CANVAS,
  GEMINI_DARK_ON_PRIMARY,
  GEMINI_DARK_PRIMARY,
  GEMINI_DARK_PRIMARY_DIM,
  GEMINI_DARK_SURFACE,
  GEMINI_DARK_SURFACE_ELEVATED,
  GEMINI_DARK_TEXT_PRIMARY,
  GEMINI_DARK_TEXT_SECONDARY,
  GEMINI_GREEN,
  GEMINI_GREEN_DARK,
  GEMINI_RED,
  GEMINI_RED_DARK,
} from "../../theme/geminiPalette";

const colors = {
  background: {
    default: GEMINI_DARK_CANVAS,
    paper: GEMINI_DARK_SURFACE,
    card: GEMINI_DARK_SURFACE,
    sidenav: GEMINI_DARK_SURFACE_ELEVATED,
    cardBorder: GEMINI_DARK_BORDER,
    cardBorderLeft: GEMINI_DARK_PRIMARY,
    section: GEMINI_DARK_SURFACE,
    sectionAlt: GEMINI_DARK_SURFACE_ELEVATED,
  },

  text: {
    main: GEMINI_DARK_TEXT_PRIMARY,
    focus: "#ffffff",
    primary: GEMINI_DARK_TEXT_PRIMARY,
    secondary: GEMINI_DARK_TEXT_SECONDARY,
    disabled: "rgba(227, 227, 227, 0.38)",
  },

  divider: GEMINI_DARK_BORDER,

  transparent: {
    main: "rgba(0, 0, 0, 0)",
  },

  white: {
    main: GEMINI_DARK_TEXT_PRIMARY,
    focus: "#ffffff",
  },

  black: {
    light: GEMINI_DARK_TEXT_SECONDARY,
    main: GEMINI_DARK_CANVAS,
    focus: "#000000",
  },

  primary: {
    main: GEMINI_DARK_PRIMARY,
    focus: GEMINI_DARK_PRIMARY_DIM,
    contrastText: GEMINI_DARK_ON_PRIMARY,
  },

  secondary: {
    main: GEMINI_DARK_TEXT_SECONDARY,
    focus: "#bdc1c6",
    contrastText: GEMINI_DARK_CANVAS,
  },

  info: {
    main: GEMINI_DARK_PRIMARY,
    focus: GEMINI_DARK_PRIMARY_DIM,
    contrastText: GEMINI_DARK_ON_PRIMARY,
  },

  success: {
    main: "#81c995",
    focus: GEMINI_GREEN,
    contrastText: GEMINI_DARK_ON_PRIMARY,
  },

  warning: {
    main: GEMINI_AMBER,
    focus: GEMINI_AMBER_DARK,
    contrastText: "#000000",
  },

  error: {
    main: "#f28b82",
    focus: GEMINI_RED,
    contrastText: GEMINI_DARK_ON_PRIMARY,
  },

  light: {
    main: GEMINI_DARK_TEXT_SECONDARY,
    focus: "#bdc1c6",
  },

  dark: {
    main: GEMINI_DARK_SURFACE,
    focus: GEMINI_DARK_CANVAS,
  },

  grey: {
    50: "#f8f9fa",
    100: "#e8eaed",
    200: "#dadce0",
    300: "#bdc1c6",
    400: "#9aa0a6",
    500: "#80868b",
    600: "#5f6368",
    700: "#3c4043",
    800: "#2d2d2d",
    900: "#1f1f1f",
  },

  priority: {
    critical: { main: "#f28b82", focus: GEMINI_RED_DARK },
    high: { main: GEMINI_AMBER, focus: GEMINI_AMBER_DARK },
    medium: { main: GEMINI_DARK_PRIMARY, focus: GEMINI_DARK_PRIMARY_DIM },
    low: { main: "#81c995", focus: GEMINI_GREEN_DARK },
  },

  status: {
    new: { main: GEMINI_DARK_PRIMARY, focus: GEMINI_DARK_PRIMARY_DIM },
    assigned: { main: GEMINI_AMBER, focus: GEMINI_AMBER_DARK },
    inProgress: { main: "#c58af9", focus: "#9334e6" },
    onHold: { main: "#9aa0a6", focus: "#5f6368" },
    resolved: { main: "#81c995", focus: GEMINI_GREEN_DARK },
    closed: { main: "#80868b", focus: "#5f6368" },
    cancelled: { main: "#f28b82", focus: GEMINI_RED_DARK },
  },

  gradients: {
    primary: { main: GEMINI_DARK_PRIMARY, state: GEMINI_DARK_PRIMARY_DIM },
    secondary: { main: "#9aa0a6", state: "#5f6368" },
    info: { main: GEMINI_DARK_PRIMARY, state: GEMINI_DARK_PRIMARY_DIM },
    success: { main: "#81c995", state: GEMINI_GREEN },
    warning: { main: GEMINI_AMBER, state: GEMINI_AMBER_DARK },
    error: { main: "#f28b82", state: GEMINI_RED },
    light: { main: GEMINI_DARK_TEXT_SECONDARY, state: "#80868b" },
    dark: { main: GEMINI_DARK_SURFACE, state: GEMINI_DARK_CANVAS },
  },

  socialMediaColors: {
    facebook: { main: "#1877f2", dark: "#166fe5" },
    twitter: { main: "#1da1f2", dark: "#1a91da" },
    instagram: { main: "#e4405f", dark: "#d93a57" },
    linkedin: { main: "#0077b5", dark: "#005885" },
    pinterest: { main: "#bd081c", dark: "#8c0615" },
    youtube: { main: "#ff0000", dark: "#cc0000" },
    vimeo: { main: "#1ab7ea", dark: "#13a3d2" },
    slack: { main: "#4a154b", dark: "#350d36" },
    dribbble: { main: "#ea4c89", dark: "#e73177" },
    github: { main: "#24292e", dark: "#171a1d" },
    reddit: { main: "#ff4500", dark: "#e03d00" },
    tumblr: { main: "#35465c", dark: "#2a3749" },
  },

  badgeColors: {
    primary: { background: "#394457", text: GEMINI_DARK_PRIMARY },
    secondary: { background: GEMINI_DARK_SURFACE_ELEVATED, text: GEMINI_DARK_TEXT_SECONDARY },
    info: { background: "#394457", text: GEMINI_DARK_PRIMARY },
    success: { background: "#2d4438", text: "#81c995" },
    warning: { background: "#4a4030", text: GEMINI_AMBER },
    error: { background: "#5c3a38", text: "#f28b82" },
    light: { background: GEMINI_DARK_SURFACE_ELEVATED, text: GEMINI_DARK_TEXT_SECONDARY },
    dark: { background: GEMINI_DARK_SURFACE, text: GEMINI_DARK_TEXT_PRIMARY },
  },

  coloredShadows: {
    primary: GEMINI_DARK_PRIMARY,
    secondary: GEMINI_DARK_TEXT_SECONDARY,
    info: GEMINI_DARK_PRIMARY,
    success: "#81c995",
    warning: GEMINI_AMBER,
    error: "#f28b82",
    light: "#80868b",
    dark: GEMINI_DARK_CANVAS,
  },

  inputBorderColor: GEMINI_DARK_BORDER,

  tabs: {
    indicator: { boxShadow: GEMINI_DARK_BORDER },
  },
};

export default colors;
